import { Dot } from "@/components/Dot";
import { useTokenCosts } from "@/design-system/Evolution/useTokenCosts";
import { CoinIcon } from "@/designSystemIcons";
import { expire as expireIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { costTokens, formatEvoCost } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { EvolutionMetaData } from "@repo/shared/types";
import { formatTime } from "@repo/shared/utils";
import { Lock } from "lucide-react";
import { Fragment, useMemo } from "react";

interface EvoDataRow {
  evolutionData?: EvolutionMetaData;
  gap?: "gap-x-2" | "gap-x-1" | "justify-between" | "justify-around";
  size?: "small" | "medium";
  hideFields?: (
    | "coins"
    | "points"
    | "requiredGames"
    | "timeRemaining"
    | "unlockBefore"
    | "free"
  )[];
  /** "inherit" takes the colour from the parent, for 2.0 hosts. */
  variant?: "secondary" | "tertiary" | "inherit";
  /** Games read "0 games" instead of a bare number. */
  labelGames?: boolean;
  className?: string;
}

export const EvoDataRow = ({
  evolutionData,
  gap = "gap-x-2",
  size = "small",
  hideFields = [],
  variant = "secondary",
  labelGames = false,
  className,
}: EvoDataRow) => {
  const { t } = useTranslation(["evolutions", "common"]);
  const timeRemaining = useMemo(() => {
    if (!evolutionData?.completeBy) return null;
    return formatTime(evolutionData.completeBy * 1000 - Date.now());
  }, [evolutionData?.completeBy]);
  const unlockBefore = useMemo(() => {
    if (!evolutionData?.unlockBy) return null;
    return formatTime(evolutionData.unlockBy * 1000 - Date.now());
  }, [evolutionData?.unlockBy]);
  const tokenCosts = useTokenCosts(costTokens(evolutionData?.cost));
  // A chain that costs tokens isn't free, so it has no "Free" coin entry.
  const showCoins = evolutionData?.cost?.coins > 0 || tokenCosts.length === 0;
  const showPoints =
    evolutionData?.cost?.points != null &&
    evolutionData?.cost.points > 0 &&
    !hideFields.includes("points");

  return (
    <div
      className={cn(
        "flex flex-row items-center w-full justify-center shrink-0",
        variant === "secondary" && "text-dark-secondary",
        variant === "tertiary" && "text-dark-tertiary",
        size === "small" ? "text-xs" : "text-sm font-medium",
        gap,
        className,
      )}
    >
      <>
        {showCoins && (
          <div className="flex gap-1 items-center">
            <CoinIcon size={size === "small" ? 12 : 16} />
            <span>
              {evolutionData?.cost?.coins > 0
                ? formatEvoCost(evolutionData?.cost.coins)
                : t("common:free")}
            </span>
          </div>
        )}

        {showPoints && (
          <>
            {showCoins && <Dot color="bg-dark-disabled" />}
            <div className="flex gap-1 items-center">
              <div className="flex gap-0.5 items-center whitespace-nowrap">
                <img
                  alt={t("common:alt.points")}
                  className={cn(
                    size === "small" ? "size-3" : "size-4",
                    "shrink-0",
                  )}
                  src={
                    import.meta.env.VITE_APP_ASSETS_BASE_URL +
                    "/fc24/misc/points.png"
                  }
                />
                <span>{evolutionData?.cost.points}</span>
              </div>
            </div>
          </>
        )}

        {tokenCosts.map((token, index) => (
          <Fragment key={token.eventTokenId}>
            {(showCoins || showPoints || index > 0) && (
              <Dot color="bg-dark-disabled" />
            )}
            <div className="flex gap-0.5 items-center whitespace-nowrap">
              <img
                alt={token.name}
                className={cn(
                  size === "small" ? "size-3.5" : "size-4",
                  "shrink-0 object-contain",
                )}
                src={token.iconUrl}
              />
              <span>{token.amount}</span>
            </div>
          </Fragment>
        ))}

        {evolutionData?.requiredGames >= 0 &&
          !hideFields.includes("requiredGames") && (
            <>
              <Dot color="bg-dark-disabled" />
              <div className="flex gap-0.5 items-center whitespace-nowrap">
                <img
                  src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/ea/ball.png`}
                  alt={t("alt.matches")}
                  className={cn(
                    size === "small" ? "size-3.5" : "size-4",
                    "shrink-0",
                  )}
                />
                <span>
                  {labelGames
                    ? t("footer.gamesCount", {
                        count: evolutionData.requiredGames,
                      })
                    : evolutionData?.requiredGames}
                </span>
              </div>
            </>
          )}
        {timeRemaining && !hideFields.includes("timeRemaining") && (
          <>
            <Dot color="bg-dark-disabled" />
            <div className="flex gap-1 items-center">
              <div
                className={cn(
                  size === "small" ? "size-3" : "size-3.5",
                  "fill-dark-secondary shrink-0",
                )}
              >
                {expireIcon}
              </div>
              <span className="whitespace-nowrap">{timeRemaining}</span>
            </div>
          </>
        )}
        {unlockBefore && !hideFields.includes("unlockBefore") && (
          <>
            <Dot color="bg-dark-disabled" />
            <div className="flex gap-0.5 items-center whitespace-nowrap">
              <Lock
                className={cn(
                  size === "small" ? "size-3" : "size-3.5",
                  "text-dark-secondary shrink-0",
                )}
              />
              <span>{unlockBefore}</span>
            </div>
          </>
        )}
      </>
    </div>
  );
};
